import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { customSignupFormRules } from 'helpers/customFormRules';
import { deleteSearchParam } from 'helpers/deleteSearchParam';

import {
  AccountManager,
  btag,
  getActiveForms,
  isUserNeedVerificationTerminals,
  Kyc,
  NotificationManager,
  pushToDataLayer,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useOutsideClick,
  useStaticBlock,
  VerificationManager,
} from '@atlas/core';
import { onSignInSubmit, useActiveForm } from '@atlas/react-forms';
import { casinoRoutes, CmdLink, staticRoutes } from '@atlas/routing';
import { BasePopup, HeaderTitle, Icon, Label } from '@atlas/ui';

import {
  BTAG_STORAGE_KEY,
  CXFROM_STORAGE_KEY,
  OAUTH_FLOW_TYPES,
  SIGN_IN_POPUP_STORAGE_KEY,
  SIGN_IN_REDIRECT_URL_QUERY_KEY,
} from 'common/constants';
import FormWithCaptcha from 'common/Form/FormWithCaptcha/FormWithCaptcha';
import {
  getMultiPopupConfig,
  showMultiPopup,
} from 'common/Notifications/Popups/MultiPopup/MultiPopupManager';
import StaticBlock from 'common/StaticBlock/StaticBlock';
import { camelCase } from 'common/utils';
import HeaderLogoItem from 'components/Layout/Header/HeaderLogoItem';
import history from 'components/Layout/history';
import Navigation from 'components/SignIn/Navigation';
import { useHidePopupSignUpDelay } from 'hooks/useHidePopupSignUpDelay';
import { useSetNextShowPopupSignUpDelay } from 'hooks/useSetNextShowPopupSignUpDelay';
import SocialProviders from 'src/components/SignUp/SocialProviders';
import { useSignupPopup } from 'src/components/SignUp/useSignUpPopup';
import useSocialProviders from 'src/components/SignUp/useSocialProviders';

import { resolveSignInAutoComplete } from './formAutocomplete';

const { signup } = staticRoutes;
const { game: casinoGamePath } = casinoRoutes;
const SIGN_IN_CLEANUP_PARAMS = ['cmd', 'path', SIGN_IN_REDIRECT_URL_QUERY_KEY];
const resolveAutoComplete = field => {
  return resolveSignInAutoComplete(field);
};

const SignIn = () => {
  const applicationState = useApplicationState();
  const authenticated = applicationState && applicationState.authenticated;
  const resolved = applicationState && applicationState.resolved;
  const cxfromPath = StorageManager.getItem(CXFROM_STORAGE_KEY);
  const { content: bannerContent } = useStaticBlock('desktop_signin_banner');

  const hasBanner = useMemo(() => {
    if (!bannerContent) return false;
    const hasImage = /<img[^>]*>/.test(bannerContent);
    const hasText = bannerContent.replace(/<[^>]*>|&nbsp;/g, '').trim().length > 0;
    return hasImage || hasText;
  }, [bannerContent]);

  const { goBack, replace: historyReplace } = useHistory();
  const { pathname, search, state } = useLocation();

  const socialRedirectPath =
    new URLSearchParams(search).get(SIGN_IN_REDIRECT_URL_QUERY_KEY) ||
    (cxfromPath !== `/${signup}` ? cxfromPath : null);

  const { activeStep, activeType, setActiveType, submit, types } = useActiveForm(
    'CLIENT_LOGIN',
    onSignInSubmit(AccountManager.loginWithForm.bind(AccountManager)),
    getActiveForms,
  );

  const { openSignupPopup } = useSignupPopup();

  const showSignUpPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup');

  useHidePopupSignUpDelay();
  useSetNextShowPopupSignUpDelay();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const path = params.get('path');

    const newActiveType = types.find(({ title }) => path === camelCase(title));

    if (newActiveType === activeType) {
      return;
    }

    if (newActiveType) {
      setActiveType(newActiveType);
    } else if (activeType) {
      params.set('path', camelCase(activeType.title));

      historyReplace(`${pathname}?${params.toString()}`, state);
    }
  }, [historyReplace, pathname, activeType, types, search, setActiveType, state]);

  const handleForgotPasswordClick = useCallback(() => {
    pushToDataLayer('forgot_password_click', btag);
  }, []);

  const forgotPasswordRedirect = useMemo(
    () => (
      <CmdLink cmd="forgotPassword" onClick={handleForgotPasswordClick}>
        <Label message="forgot_password" />
      </CmdLink>
    ),
    [handleForgotPasswordClick],
  );

  const socialProviders = useSocialProviders(activeType);

  const signInFooter = useMemo(
    () => (
      <div className="signin-footer">
        {activeStep === 0 && (
          <SocialProviders
            flowType={OAUTH_FLOW_TYPES.SIGNIN}
            items={socialProviders}
            redirectPath={socialRedirectPath}
          />
        )}
        {SiteConfigManager.getConfig('config.config.enable_registration') && (
          <>
            {showSignUpPopup ? (
              <div className="signin-footer__signup">
                <Label className="help-text" message="not_member" />
                <button
                  className="title-bold-text link"
                  onClick={() => {
                    StorageManager.removeItem(CXFROM_STORAGE_KEY);
                    openSignupPopup();
                  }}
                >
                  <Label message="registration" />
                </button>
              </div>
            ) : (
              <div className="signin-footer__signup">
                <Link onClick={() => StorageManager.removeItem(CXFROM_STORAGE_KEY)} to={signup}>
                  <Label className="title-bold-text link" message="Registration" />
                </Link>
              </div>
            )}
          </>
        )}
        {forgotPasswordRedirect}
      </div>
    ),
    [
      activeStep,
      forgotPasswordRedirect,
      openSignupPopup,
      showSignUpPopup,
      socialProviders,
      socialRedirectPath,
    ],
  );

  const formFields = activeType?.steps[0]?.fields;
  const realFormFields = useMemo(() => {
    if (formFields) {
      return formFields.map(field => ({
        ...field,
        ...customSignupFormRules(field),
        autocomplete: resolveAutoComplete(field),
      }));
    }
    return formFields;
  }, [formFields]);

  const terminalKycVerification = async data => {
    const { type: provider } = await Kyc.getKycVerificationType();
    const { initAttributes } = await Kyc.initKycProvider({
      cpf: data.client.docNumber,
      kycType: provider,
    });

    const options = {
      attributes: initAttributes,
      cpf: data.client.docNumber,
      provider,
    };

    await VerificationManager.startVerification(options);

    NotificationManager.notify({
      message: 'withdrawal_verification_completed_successfully',
      status: 'success',
      type: 'notification',
    });
  };

  const submitHandler = React.useCallback(
    async data => {
      if (state?.terminal_kyc_verification && isUserNeedVerificationTerminals(data.client)) {
        await terminalKycVerification(data);
      }

      const params = new URLSearchParams(search);
      const redirectUrl = params.get(SIGN_IN_REDIRECT_URL_QUERY_KEY);
      const signinMultiPopupConfig = getMultiPopupConfig('sign_in');
      const signinMultiPopupCallFn = cb => () => {
        cb && cb();
        showMultiPopup('sign_in', SIGN_IN_POPUP_STORAGE_KEY);
      };

      if (data && data.is2FaEnabled) {
        NotificationManager.display({
          // title: 'edit_bet_success_title',
          // message: 'edit_bet_success_message',
          data: {
            ...data,
            onClose: signinMultiPopupCallFn(() => {
              historyReplace(redirectUrl || cxfromPath || history.pathname);
              StorageManager.removeItem(CXFROM_STORAGE_KEY);
              StorageManager.removeItem(BTAG_STORAGE_KEY);
            }),
          },
          type: 'twoFactorLogin',
        });

        return;
      }

      if (redirectUrl) {
        historyReplace(redirectUrl);

        if (pathname.includes(casinoGamePath)) {
          location.reload();
        }
      } else {
        if (cxfromPath) {
          historyReplace(cxfromPath);
          StorageManager.removeItem(CXFROM_STORAGE_KEY);
          StorageManager.removeItem(BTAG_STORAGE_KEY);
        }

        const redirect = params.get('redirect');

        if (!cxfromPath && redirect) {
          goBack();
        }
      }

      if (SiteConfigManager.getConfig('config.config.responsible_gaming.show_popup')) {
        NotificationManager.display({
          data: {
            onClose: signinMultiPopupCallFn(),
          },
          type: 'responsibleGaming',
        });
      } else if (signinMultiPopupConfig) {
        signinMultiPopupCallFn()();
      }
    },
    [state?.terminal_kyc_verification, search, historyReplace, cxfromPath, pathname, goBack],
  );

  const onOutsideClick = () => {
    StorageManager.removeItem(CXFROM_STORAGE_KEY);
    const cleanSearch = deleteSearchParam(history.location.search, SIGN_IN_CLEANUP_PARAMS);
    history.push({ pathname, search: cleanSearch });
  };

  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef, onOutsideClick);

  if (resolved && authenticated) {
    const cleanSearch = deleteSearchParam(search, SIGN_IN_CLEANUP_PARAMS);
    return <Redirect to={{ pathname, search: cleanSearch }} />;
  }

  const closeSignIn = () => {
    const newUrl = deleteSearchParam(history.location.search, SIGN_IN_CLEANUP_PARAMS);

    StorageManager.removeItem(CXFROM_STORAGE_KEY);

    history.replace({
      search: newUrl,
    });
  };

  const renderForm = activeType ? (
    <FormWithCaptcha
      action={submit}
      bottomContent={signInFooter}
      buttonClassName="block"
      buttonId="signIn"
      buttonText="sign_in"
      form={{ fields: realFormFields, number: activeStep }}
      formType={activeType}
      id={activeType.id}
      onSubmit={submitHandler}
      validateRequestHeaders={{ 'Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone }}
    />
  ) : null;

  return (
    <BasePopup className={cx('signin-modal', { 'no-banner': !hasBanner })} ref={wrapperRef}>
      {hasBanner && (
        <div className="signin-popup__with_banner">
          <StaticBlock options={{ source: 'desktop_signin_banner' }} />
        </div>
      )}
      <div className="signin-popup-wrapper">
        <div className="header-title-wrapper">
          {hasBanner ? (
            <HeaderLogoItem logoPosition="center" />
          ) : (
            <HeaderLogoItem logoPosition="center" />
          )}
          <div className="header-title__children">
            <div
              onClick={closeSignIn}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  closeSignIn();
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Icon name="close" />
            </div>
          </div>
        </div>
        <div className="popup__header">
          <HeaderTitle className="center border block-lg size-xl" title="sign-in" />
        </div>
        <Navigation types={types} />
        <div className="popup__content">{renderForm}</div>
      </div>
    </BasePopup>
  );
};

export default SignIn;
